import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCw,
  KeyRound,
  Clock,
  Copy,
  Crown,
  User as UserIcon,
  Users2,
  Eye,
  EyeOff,
  Send,
  XCircle,
  ScrollText,
  ShieldAlert,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/ui/PasswordInput";

function fmtWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function minutesLeft(iso) {
  if (!iso) return null;
  const diff = (new Date(iso).getTime() - Date.now()) / 60000;
  return Math.max(0, Math.floor(diff));
}

/**
 * Pending Password Reset Codes — support console for MASTER_ADMIN,
 * MASTER_AGENT and authorised COLLABORATORs.
 *
 * All rows are keyed to `pending_password_reset_otps` (NOT the login OTP
 * collection). Reveal / Resend / Reject actions are gated on the backend
 * by hierarchy + collaborator permissions.
 */
export default function PendingPasswordResetCodes() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "MASTER_ADMIN";

  const ROLE_BADGES = {
    MASTER_AGENT: { label: t("role.MASTER_AGENT"), cls: "bg-violet-50 text-violet-700 border-violet-200", Icon: Crown },
    AGENT: { label: t("role.AGENT"), cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: UserIcon },
    COLLABORATOR: { label: "Collaborator", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Users2 },
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);          // any per-row action in flight
  const [revealDlg, setRevealDlg] = useState(null);    // { id }
  const [revealPw, setRevealPw] = useState("");
  const [revealReason, setRevealReason] = useState("");
  const [rejectDlg, setRejectDlg] = useState(null);    // { id }
  const [rejectReason, setRejectReason] = useState("");
  const [auditDlg, setAuditDlg] = useState(null);      // { id, events, reveals }

  // { [id]: { code, secondsLeft } }
  const [revealed, setRevealed] = useState({});
  const revealTimers = useRef({});
  useEffect(() => () => {
    Object.values(revealTimers.current).forEach((t) => clearInterval(t));
    revealTimers.current = {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending-password-reset-codes");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("pw_pending.failed_load", { defaultValue: "Failed to load password-reset codes" }));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const startRevealCountdown = (id, seconds) => {
    setRevealed((r) => ({ ...r, [id]: { code: r[id]?.code, secondsLeft: seconds } }));
    if (revealTimers.current[id]) clearInterval(revealTimers.current[id]);
    revealTimers.current[id] = setInterval(() => {
      setRevealed((r) => {
        const cur = r[id];
        if (!cur) return r;
        const left = cur.secondsLeft - 1;
        if (left <= 0) {
          clearInterval(revealTimers.current[id]);
          delete revealTimers.current[id];
          const { [id]: _hidden, ...rest } = r;
          return rest;
        }
        return { ...r, [id]: { ...cur, secondsLeft: left } };
      });
    }, 1000);
  };

  const doReveal = async () => {
    if (!revealDlg?.id || !revealPw) return;
    const id = revealDlg.id;
    setBusyId(id);
    try {
      const { data } = await api.post(`/pending-password-reset-codes/${id}/reveal`, {
        password: revealPw,
        reason: revealReason || undefined,
      });
      setRevealed((r) => ({ ...r, [id]: { code: data.otp, secondsLeft: data.display_seconds || 20 } }));
      startRevealCountdown(id, data.display_seconds || 20);
      setRevealDlg(null);
      setRevealPw("");
      setRevealReason("");
      toast.success(t("pw_pending.revealed", { defaultValue: "Code revealed for {{s}} seconds", s: data.display_seconds || 20 }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusyId(null);
    }
  };

  const doResend = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/pending-password-reset-codes/${id}/resend`, {});
      toast.success(t("pw_pending.resent", { defaultValue: "SMS resent ({{n}}/3)", n: data.sends_used }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async () => {
    if (!rejectDlg?.id) return;
    const id = rejectDlg.id;
    setBusyId(id);
    try {
      await api.post(`/pending-password-reset-codes/${id}/reject`, { reason: rejectReason || undefined });
      toast.success(t("pw_pending.rejected", { defaultValue: "Request rejected" }));
      setRejectDlg(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusyId(null);
    }
  };

  const openAudit = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.get(`/pending-password-reset-codes/${id}/audit`);
      setAuditDlg({ id, events: data.events || [], reveals: data.reveals || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="space-y-6" data-testid="pw-pending-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {t("pw_pending.title", { defaultValue: "Pending Password Reset Codes" })}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t("pw_pending.sub", {
              defaultValue:
                "Password-reset OTPs are separate from login codes. Reveal is audited; codes are hidden by default.",
            })}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-white border border-slate-300 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
          data-testid="pw-pending-refresh"
        >
          <RefreshCw className={"w-4 h-4 " + (loading ? "animate-spin" : "")} />
          {t("common.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            {t("common.loading", { defaultValue: "Loading…" })}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="pw-pending-empty">
            {t("pw_pending.empty", { defaultValue: "No pending password-reset codes." })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Phone (masked)</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">Expires</th>
                  <th className="text-left px-4 py-3">SMS</th>
                  <th className="text-left px-4 py-3">Attempts</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => {
                  const badge = ROLE_BADGES[r.role] || ROLE_BADGES.AGENT;
                  const RoleIcon = badge.Icon;
                  const rev = revealed[r.id];
                  const mLeft = minutesLeft(r.expires_at);
                  return (
                    <tr key={r.id} data-testid={`pw-pending-row-${r.id}`} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${badge.cls}`}>
                            <RoleIcon className="w-3 h-3" /> {badge.label}
                          </span>
                          <span className="font-semibold text-slate-900" data-testid={`pw-pending-username-${r.id}`}>{r.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-[13px]" data-testid={`pw-pending-phone-${r.id}`}>{r.phone_masked}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtWhen(r.created_at)}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span>{mLeft != null ? `${mLeft} min left` : "—"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <SmsBadge status={r.sms_status} sid={r.twilio_message_sid} isAdmin={isAdmin} errCode={r.sms_error_code} />
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          Sends: {r.sms_send_count}/{r.max_sends}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <span className={r.failed_attempts >= r.max_attempts ? "text-rose-600 font-semibold" : ""}>
                          {r.failed_attempts}/{r.max_attempts}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                          {rev?.code ? (
                            <div
                              data-testid={`pw-pending-revealed-${r.id}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border-2 border-emerald-500 bg-emerald-50 text-emerald-700 font-mono font-bold text-sm"
                            >
                              <Eye className="w-3.5 h-3.5" /> {rev.code}
                              <span className="text-[10px] text-emerald-600 ml-1">
                                ({rev.secondsLeft}s)
                              </span>
                              <button
                                title="Copy"
                                onClick={() => { navigator.clipboard.writeText(rev.code); toast.success("Copied"); }}
                                className="ml-1 text-emerald-700 hover:text-emerald-900"
                                data-testid={`pw-pending-copy-${r.id}`}
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setRevealDlg({ id: r.id }); setRevealPw(""); setRevealReason(""); }}
                              disabled={busyId === r.id}
                              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
                              data-testid={`pw-pending-reveal-${r.id}`}
                            >
                              <EyeOff className="w-3.5 h-3.5" /> Reveal
                            </button>
                          )}
                          <button
                            onClick={() => doResend(r.id)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
                            data-testid={`pw-pending-resend-${r.id}`}
                            title="Resend SMS"
                          >
                            <Send className="w-3.5 h-3.5" /> Resend
                          </button>
                          <button
                            onClick={() => { setRejectDlg({ id: r.id }); setRejectReason(""); }}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-rose-700 border border-rose-300 bg-white hover:bg-rose-50 disabled:opacity-60"
                            data-testid={`pw-pending-reject-${r.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                          <button
                            onClick={() => openAudit(r.id)}
                            disabled={busyId === r.id}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-60"
                            data-testid={`pw-pending-audit-${r.id}`}
                          >
                            <ScrollText className="w-3.5 h-3.5" /> Audit
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reveal dialog */}
      {revealDlg && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pw-pending-reveal-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              <h3 className="text-lg font-bold text-slate-900">Reveal password-reset code</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Re-enter your password. The code will be visible for 20 seconds and every reveal is audited.
            </p>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Your password</label>
            <PasswordInput
              data-testid="pw-pending-reveal-pw"
              value={revealPw}
              onChange={(e) => setRevealPw(e.target.value)}
              autoFocus
              className="flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            />
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-4 mb-1.5">
              Reason (optional)
            </label>
            <input
              data-testid="pw-pending-reveal-reason"
              type="text"
              value={revealReason}
              onChange={(e) => setRevealReason(e.target.value)}
              maxLength={200}
              className="flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              placeholder="e.g. lost phone / verified caller ID"
            />
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setRevealDlg(null)}
                className="h-10 px-4 rounded-lg bg-white border border-slate-300 text-sm font-semibold hover:bg-slate-50"
                data-testid="pw-pending-reveal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doReveal}
                disabled={!revealPw || busyId === revealDlg.id}
                className="h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-60"
                data-testid="pw-pending-reveal-confirm"
              >
                {busyId === revealDlg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reveal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject dialog */}
      {rejectDlg && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pw-pending-reject-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Reject password-reset request</h3>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Reason (optional)</label>
            <input
              data-testid="pw-pending-reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={200}
              className="flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              placeholder="e.g. suspicious activity / user not verified"
            />
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setRejectDlg(null)}
                className="h-10 px-4 rounded-lg bg-white border border-slate-300 text-sm font-semibold hover:bg-slate-50"
                data-testid="pw-pending-reject-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doReject}
                disabled={busyId === rejectDlg.id}
                className="h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-60"
                data-testid="pw-pending-reject-confirm"
              >
                {busyId === rejectDlg.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit dialog */}
      {auditDlg && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="pw-pending-audit-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-900 mb-2 flex items-center gap-2">
              <ScrollText className="w-5 h-5" /> Audit history
            </h3>
            <div className="text-xs text-slate-500 mb-4">Request ID: <span className="font-mono">{auditDlg.id}</span></div>
            <div className="space-y-2">
              {auditDlg.events.length === 0 ? (
                <div className="text-sm text-slate-500">No audit events yet.</div>
              ) : (
                auditDlg.events.map((ev) => (
                  <div key={ev.id || ev.created_at} className="text-xs border-l-2 border-slate-200 pl-3 py-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{ev.action}</span>
                      <span
                        className={
                          "text-[10px] px-1.5 py-0.5 rounded font-semibold " +
                          (ev.status === "FAILED"
                            ? "bg-rose-50 text-rose-700"
                            : "bg-emerald-50 text-emerald-700")
                        }
                      >
                        {ev.status || "SUCCESS"}
                      </span>
                      <span className="text-slate-400">{fmtWhen(ev.created_at)}</span>
                    </div>
                    {ev.actor_username && (
                      <div className="text-slate-500">by {ev.actor_username} ({ev.actor_role})</div>
                    )}
                  </div>
                ))
              )}
              {auditDlg.reveals.length > 0 && (
                <>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mt-4 mb-1">Reveal audit</div>
                  {auditDlg.reveals.map((rv) => (
                    <div key={rv.id} className="text-xs border-l-2 border-amber-200 pl-3 py-1">
                      <div className="text-slate-800 font-semibold">Revealed by {rv.viewer_username} ({rv.viewer_role})</div>
                      <div className="text-slate-500">{fmtWhen(rv.revealed_at)} · IP {rv.viewer_ip || "—"}</div>
                      {rv.reason && <div className="text-slate-500">reason: {rv.reason}</div>}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setAuditDlg(null)}
                className="h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6]"
                data-testid="pw-pending-audit-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SmsBadge({ status, sid, isAdmin, errCode }) {
  const map = {
    SMS_ACCEPTED: { cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Accepted" },
    SMS_DELIVERED: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Delivered" },
    SMS_FAILED: { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Failed" },
    SMS_UNDELIVERED: { cls: "bg-rose-50 text-rose-700 border-rose-200", label: "Undelivered" },
  };
  const info = map[status] || { cls: "bg-slate-100 text-slate-600 border-slate-200", label: status || "—" };
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${info.cls}`}>
        {info.label}
      </span>
      {isAdmin && sid && <span className="text-[10px] text-slate-400 font-mono truncate max-w-[80px]" title={sid}>{sid.slice(0, 8)}…</span>}
      {isAdmin && errCode && <span className="text-[10px] text-rose-500">#{errCode}</span>}
    </div>
  );
}
